import { IconDefinition } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useOnClickOutside from 'hooks/useCloseOnClickOutside';
import Spinner from './Spinner';
import Text from './Text';
import { useEffect } from 'react';
import styled from 'styled-components';
import { Ul } from './List';

export type ItemListAction = {
    onClick: () => void;
    label?: string;
    subLabel?: string;
    icon?: IconDefinition;
};

interface ItemsListProps<T> {
    items: T[];
    thin?: boolean;
    maxHeight?: number;
    resultTitle?: string | null;
    childrenTitleKey?: keyof T;
    childrenSubtitleKey?: keyof T;
    /** Type needs to be IconDefinition */
    childrenIconKey?: keyof T;
    showColorDotFromHex?: boolean;
    /** Defaults to bottom right, pass null for position relative. */
    absolutePositioned?: Pos | null;
    colorKey?: keyof T;
    focusedItemRow?: number;
    onFocusedItemRowChange?: (item: number) => void;
    onEnter?: (e: KeyboardEvent, item?: T) => void;
    onBackspace?: (e: KeyboardEvent) => void;
    onItemPress: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, item: T) => void;
    onClickOutside?: () => void;
    pending?: boolean;
    className?: string;
    /** Use item max content width instead of parent width for the itemlist. */
    breakParentWidth?: boolean;
}

const ItemsList = <T,>({
    items,
    thin,
    maxHeight,
    resultTitle,
    childrenTitleKey,
    childrenSubtitleKey,
    childrenIconKey,
    showColorDotFromHex,
    absolutePositioned = { top: '100%', left: '0' },
    colorKey,
    focusedItemRow = -1,
    onFocusedItemRowChange,
    onEnter,
    onBackspace,
    onItemPress,
    onClickOutside,
    pending,
    breakParentWidth,
    ...props
}: ItemsListProps<T>) => {
    const ref = useOnClickOutside<HTMLUListElement>(() => onClickOutside?.());

    // Keyboard shortcuts.
    useEffect(() => {
        if (items.length < 1) return;

        const onKey = (e: KeyboardEvent): void => {
            const key = e.key;
            switch (key) {
                case 'ArrowUp':
                case 'ArrowDown': // Navigation on up and down key.
                    e.preventDefault();
                    if (key === 'ArrowDown' && focusedItemRow >= items.length - 1) {
                        onFocusedItemRowChange?.(0);
                    } else if (key === 'ArrowUp' && focusedItemRow === 0) {
                        onFocusedItemRowChange?.(items.length - 1);
                    } else {
                        onFocusedItemRowChange?.(focusedItemRow + (key === 'ArrowUp' ? -1 : +1));
                    }
                    // If max height is defined, autoscroll list if needed.
                    if (maxHeight) {
                        const el = ref?.current?.getBoundingClientRect();
                        const child = ref?.current?.children?.[focusedItemRow]?.getBoundingClientRect();
                        if (el != null && child != null) {
                            if (key === 'ArrowUp' && focusedItemRow === 0) {
                                ref?.current?.scrollTo(0, ref?.current?.scrollHeight);
                            } else if (key === 'ArrowDown' && focusedItemRow >= items.length - 1) {
                                ref?.current?.scrollTo(0, 0);
                            } else if (key === 'ArrowDown' && child.bottom + child.height > el.bottom) {
                                ref?.current?.scrollTo(0, ref?.current?.scrollTop + child?.height);
                            } else if (key === 'ArrowUp' && child.top - child.height < el.top) {
                                ref?.current?.scrollTo(0, ref?.current?.scrollTop - child?.height);
                            }
                        }
                    }
                    break;
                case 'Enter':
                    onEnter?.(e, items[focusedItemRow]);
                    break;
                case 'Backspace':
                    onBackspace?.(e);
                    break;
            }
        };

        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [items, focusedItemRow, maxHeight, onEnter, onBackspace, onFocusedItemRowChange, ref]);

    return (
        <SearchResult
            breakParentWidth={breakParentWidth}
            ref={ref}
            show={items.length >= 1 || pending}
            role="menu"
            maxHeight={maxHeight}
            absolutePositioned={absolutePositioned}
            {...props}
        >
            {resultTitle && <ResultTitle className="f1-500">{resultTitle}</ResultTitle>}
            {items.map((item, i) => {
                const color =
                    showColorDotFromHex &&
                    colorKey &&
                    typeof item[colorKey] === 'string' &&
                    ((item[colorKey] as unknown) as string);

                return (
                    <Item
                        key={`items-list-item-${i}`}
                        tabIndex={0}
                        role="menuitem"
                        type="button"
                        thin={thin}
                        onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            onItemPress(e, item);
                        }}
                        focused={focusedItemRow === i}
                        onMouseEnter={() => onFocusedItemRowChange?.(i)}
                    >
                        {childrenTitleKey && item[childrenTitleKey] && (
                            <TitleWrapper>
                                {color && <ColorDot color={color} />}
                                {childrenIconKey && item[childrenIconKey] && (
                                    <FontAwesomeIcon icon={(item[childrenIconKey] as unknown) as IconDefinition} />
                                )}
                                {/* TODO! Ta bort och typa bättre! */}
                                <Title>{item[childrenTitleKey] as string}</Title>
                            </TitleWrapper>
                        )}
                        {childrenSubtitleKey && item[childrenSubtitleKey] && (
                            <Text fontSize={13}>{item[childrenSubtitleKey] as string}</Text>
                        )}
                        {typeof item === 'string' && item}
                    </Item>
                );
            })}
            {pending && (
                <div className="p-1">
                    <Spinner />
                </div>
            )}
        </SearchResult>
    );
};

type Pos = { top?: string; bottom?: string; left?: string; right?: string };
const SearchResult = styled(Ul)<{
    absolutePositioned?: Pos | null;
    show?: boolean;
    maxHeight?: number;
    breakParentWidth?: boolean;
}>`
    background-color: white;
    transition: opacity 0.2s ease, visibility 0.2s ease, transform 0.2s ease-in-out;
    width: ${({ breakParentWidth }) => (breakParentWidth ? 'max-content' : '100%')};

    ${({ absolutePositioned: abs }) =>
        abs
            ? `
          position: absolute;
          ${abs.top && `top: ${abs.top};`};
          ${abs.bottom && `bottom: ${abs.bottom};`};
          ${abs.left && `left: ${abs.left};`};
          ${abs.right && `right: ${abs.right};`};
          box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.16);
          z-index: 1000;
        `
            : `
          position: relative;
        `};

    ${({ maxHeight }) =>
        maxHeight &&
        `
      max-height: ${maxHeight}px;
      overflow: auto;
    `}

    ${({ show }) =>
        show
            ? `
        opacity: 1;
        visibility: visible;
        transform: none;`
            : `
        opacity: 0;
        visibility: hidden;
        transform: translateY(-20px);
        transition-duration: 0.05s;
      `}
`;

const ResultTitle = styled.span`
    display: block;
    padding: 15px 15px 10px;
`;

const TitleWrapper = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;

    > *:nth-child(2) {
        margin-left: 10px;
    }
`;

const Item = styled.button<{ focused?: boolean; thin?: boolean }>`
    display: flex;
    flex-direction: column;
    padding: 7px 15px;
    border: 0;
    width: 100%;
    background-color: transparent;
    transition: background-color 0.2s ease;
    text-align: left;
    padding: ${({ thin }) => (thin ? '5px' : '7px 15px')};
    ${({ focused }) => focused && 'background: rgb(244, 244, 244);'};
    cursor: pointer;
    &:hover,
    &:active {
        background: rgb(244, 244, 244);
    }

    &:focus-within {
        outline: 0;
    }
    &:focus-visible {
        outline: 1px solid black;
    }
`;

const Title = styled.span`
    font-size: 15px;
`;

const ColorDot = styled.span<{ color: string }>`
    display: block;
    height: 15px;
    width: 15px;
    border-radius: 50%;
    background: ${({ color }) => color};
    border: 1px rgba(0, 0, 0, 0.2) solid;
`;

export default ItemsList;

export const ItemsListWrapper = styled.div`
    position: absolute;
    left: 25px;
    right: auto;
    bottom: -15px;
    z-index: 200;
    ${({ theme }) => theme.shadows.high}

    ${({ theme }) => theme.media.lg} {
        right: 25px;
        left: auto;
    }
`;
